import { useMeasure } from '@uidotdev/usehooks';
import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';

import { useTheme } from '@mui/material/styles';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { Gauge, gaugeClasses } from '@mui/x-charts/Gauge';

import { shadeHexColor } from '@/utils';

import dayjs from '@/dayjs';

import {
  ReeferKpiByModel,
  useBreakpoints,
  useFormatters,
  useReeferKpiByModel,
} from '@/hooks';

import { toRoundedMWh } from '../../ReeferInsights/components/Context/ReeferKpi';
import { getEnergyUsageInputFromFilters, store } from '../store';

export const ReeferKPIContainerContainerHeight = 300;

function ReeferKPI() {
  const { t } = useTranslation();
  const theme = useTheme();
  const snap = useSnapshot(store);
  const { isMobileDevice } = useBreakpoints();

  // used to adjust centering of element in the chart area (loading, message, ...)
  const [titleRef, { height: titleHeight }] = useMeasure();

  // generic number format
  const { formatFloat } = useFormatters();

  const params = getEnergyUsageInputFromFilters(snap as typeof store);
  const [reeferKpiResult, refresh] = useReeferKpiByModel(params);

  // get our dataset or undefined if not loaded yet
  const dataset: ReeferKpiByModel | null = reeferKpiResult.data || {
    total_active_energy_kWh: 0,
    total_co2eq_emissions: 0,
    reefer_power_on_ratio: 0,
    reefer_usage_stable_ratio: 0,
    reefer_usage_transition_ratio: 0,
  };

  const isLoading = reeferKpiResult.isLoading;
  /*
    total reefer usage in days, and % over period of time
    -> shows how much time the reefer is On for any period
   
    total stable reefer usage, in days and % of total reefer usage
    -> shows % of time running stable
    
    total Transition reefer usage, in days and %
    -> shows % of time catching up on set temp
   
    total reefer energy usage, in MWh and Tons of fuel -
   
    total distance travelled , in km - show distance when On;
  */

  const { start, end } = params;
  const start_datetime = dayjs(start).utc();
  const end_datetime = dayjs(end).utc();

  const diff_in_days = end_datetime.diff(start_datetime, 'days');

  const {
    total_active_energy_kWh,
    reefer_power_on_ratio,
    reefer_usage_stable_ratio,
    reefer_usage_transition_ratio,
  } = dataset;

  const allZeros =
    !total_active_energy_kWh &&
    !reefer_power_on_ratio &&
    !reefer_usage_stable_ratio &&
    !reefer_usage_transition_ratio;

  const gaugeProps = {
    width: 140,
    height: 140,
    innerRadius: '70%',
    sx: {
      [`& .${gaugeClasses.valueArc}`]: {
        fill: snap.isAveragePowerMode
          ? theme.palette.primary.main
          : theme.palette.secondary.main,
      },
      [`& .${gaugeClasses.referenceArc}`]: {
        fill: shadeHexColor(
          snap.isAveragePowerMode
            ? theme.palette.primary.main
            : theme.palette.secondary.main,
          0.8,
        ),
      },
    },
  };

  return (
    <Paper
      variant="elevation"
      sx={{
        height: `${ReeferKPIContainerContainerHeight}px`,
        width: '100%',
        borderRadius: 4,
      }}
    >
      <Stack
        ref={titleRef}
        direction="row"
        alignItems="flex-start"
        flexGrow={1}
        spacing={1}
        sx={{ pl: 3, pt: 2 }}
      >
        <Typography variant="h5">Reefer KPI</Typography>
        {/* <Typography variant="h5" color="GrayText">
          {snap.viewModeUnit}
        </Typography> */}
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        sx={{
          width: '100%',
          height: ReeferKPIContainerContainerHeight - (titleHeight || 0),
        }}
      >
        {isLoading && <CircularProgress />}

        {!isLoading && allZeros && (
          <Typography variant="overline" sx={{ mt: `-${titleHeight}px` }}>
            {t('noData')}
          </Typography>
        )}

        {!isLoading && !allZeros && (
          <Stack direction="column" spacing={1} sx={{ pb: 2 }}>
            <Stack
              direction="row"
              alignItems="flex-end"
              justifyContent="center"
              spacing={1}
            >
              <Typography variant="subtitle1">
                {t('emissionsDashboard.reeferKpi.totalActiveEnergy')}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h5">
                  {toRoundedMWh(total_active_energy_kWh)}
                </Typography>
                <Typography variant="h5" color="GrayText">
                  MWh
                </Typography>
              </Stack>
            </Stack>
            <Divider flexItem sx={{ width: '90%', alignSelf: 'center' }} />

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={2}
            >
              <Stack
                direction="column"
                alignItems="center"
                justifyContent="center"
              >
                <Typography variant="overline">
                  {t('operatingPhase.powerOn')}
                </Typography>
                <Gauge
                  value={
                    reefer_power_on_ratio
                      ? Math.round(reefer_power_on_ratio)
                      : 0
                  }
                  text={({ value, valueMax }) => `${value} %`}
                  {...gaugeProps}
                />
                <Stack
                  direction="column"
                  alignItems="center"
                  justifyContent="center"
                  spacing={1}
                >
                  <Typography variant="subtitle1" sx={{ visibility: 'hidden' }}>
                    -
                  </Typography>
                </Stack>
              </Stack>
              <Stack
                direction="column"
                alignItems="center"
                justifyContent="center"
              >
                <Typography variant="overline">
                  {t('operatingPhase.stable')}
                </Typography>
                <Gauge
                  value={
                    reefer_usage_stable_ratio
                      ? Math.round(reefer_usage_stable_ratio)
                      : 0
                  }
                  text={({ value, valueMax }) => `${value} %`}
                  {...gaugeProps}
                />

                <Typography variant="subtitle1">
                  {toRoundedMWh(
                    ((reefer_usage_stable_ratio || 0) / 100) *
                      (total_active_energy_kWh || 0),
                  )}{' '}
                  MWh
                </Typography>
              </Stack>
              <Stack
                direction="column"
                alignItems="center"
                justifyContent="center"
              >
                <Typography variant="overline">
                  {t('operatingPhase.transition')}
                </Typography>
                <Gauge
                  value={
                    reefer_usage_transition_ratio
                      ? Math.round(reefer_usage_transition_ratio)
                      : 0
                  }
                  text={({ value, valueMax }) => `${value} %`}
                  {...gaugeProps}
                />

                <Typography variant="subtitle1">
                  {toRoundedMWh(
                    ((reefer_usage_transition_ratio || 0) / 100) *
                      (total_active_energy_kWh || 0),
                  )}{' '}
                  MWh
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

export { ReeferKPI };
